import { days } from "../constants/days";
import {
	Flow,
	ServiceAvailabilityException,
	Reservation,
	Service,
} from "../types/entities";

// FILTERING SERVICES ----------------------------------------------------------------

// I have to show only those services that are available and that have a valid schedule (with values)
export const validateServices = (services: Service[]): Service[] => {
	const today = new Date();
	return services.filter((service) => {
		if (!service.isActive) return false;
		//I set a start date and date to compare if the service is "expired"
		if (service.startDate && service.endDate) {
			const startDate = new Date(service.startDate);
			startDate.setHours(0, 0, 0, 0);
			const endDate = new Date(service.endDate);
			endDate.setHours(23, 59, 59, 999);
			//If the service is a event, then I have to check that the start
			//and end date are bigger than today
			if (service.category.name === "Event") {
				if (startDate < today && endDate < today) return false;
			} else if (!(startDate <= today && endDate >= today)) return false;
		}
		// I check if the service has schedules with values
		if (!service.schedule) return false;
		const hasSchedules = Object.values(service.schedule).some(
			(sch) => sch.length > 0
		);
		if (!hasSchedules) return false;
		return true;
	});
};

// This function filters the services where the date received is between the startDate and endDate.
export const filterServicesByDateBetweenStartDateAndEndDate = (
	activeServices: Service[],
	date: Date
) =>
	activeServices.filter((service) =>
		service.endDate !== null
			? service.startDate! <= date && service.endDate >= date
			: service.startDate! <= date
	);

// This function filters services by the date received. It gets the services that, in their schedule,
// contains the day of the week selected.
export const filterServicesByDayOfWeek = (
	activeServices: Service[],
	date: Date
) => {
	const dayOfWeek = days[date.getDay()];
	return activeServices.filter(
		(service) =>
			// Validating the keys (days of the week) include the date of the week received and that they are not empty.
			dayOfWeek in service.schedule && service.schedule[dayOfWeek].length > 0
	);
};

// This function filters the services received by anticipation, we get the schedules of the service, and
// return only those schedules that have the required anticipation with respect to the current schedule.
export const filterServicesByAnticipation = (
	services: Service[],
	date: Date,
	schedule?: string
) => {
	let scheduleToCompare;
	// Getting all the schedules of the service ordered ascending
	return services.filter((service) => {
		const anticipation = parseInt(service.category.anticipation);
		const dayOfWeek = date.getDay();
		// Getting the last schedule of the service by day of week, because if the last schedule
		// of the service have the required anticipation, then we have to show that service.
		if (schedule) scheduleToCompare = schedule;
		else
			scheduleToCompare = service.schedule[dayOfWeek]
				? service.schedule[dayOfWeek].slice(-1)!.toString()
				: "23:59";
		//If we receive the schedule, then we compare it, otherwise we compare the last schedule
		return compareAnticipation(scheduleToCompare, date, anticipation);
	});
};

// This function verifies that the time received has the anticipation received.
const compareAnticipation = (
	time: string,
	date: Date,
	anticipation: number
) => {
	//I set a date with the received date, with the received time.
	const today = new Date();
	today.setSeconds(0);
	today.setMilliseconds(0);
	const dayToCompare = new Date(date);
	dayToCompare.setHours(
		parseInt(time.substring(0, 2)),
		parseInt(time.substring(3, 5)),
		0,
		0
	);

	// If I add the necessary anticipation to today, and it is greater than the day I am evaluating,
	// it means that I should not show that schedule because I am exceeding the anticipation.
	const todayPlusAnticipation = new Date(
		today.setHours(today.getHours() + anticipation)
	);
	return todayPlusAnticipation <= dayToCompare;
};

// This function check if the received day is disabled by all services being in the day's exception,
// by all shifts of all services being disabled or by a combination of both cases.
export const checkDisabledDate = (
	date: Date,
	services: Service[],
	serviceAvailabilityException: ServiceAvailabilityException[],
	offlineDaysByServices: { date: Date; service: Service }[]
) => {
	const todayOfflineDays = offlineDaysByServices.filter(
		(od) => od.date.getTime() === date.getTime()
	);
	const todayServicesExceptions = serviceAvailabilityException.find(
		(od) => od.date.getTime() === date.getTime()
	);

	// If all received services are in a exception, then the day is disabled
	const areAllInException = todayServicesExceptions
		? services.every((service) =>
				todayServicesExceptions.services.some((s) => service.id === s.id)
		  )
		: false;

	if (areAllInException) return true;

	// If all shifts of every service are offline, then the day is disabled
	const allAreOffline = services.every((service) =>
		todayOfflineDays.some((offlineDay) => offlineDay.service.id === service.id)
	);

	if (allAreOffline) return true;

	// If all services are or offline or in exception, then the day is disabled
	const anyServiceOfflineOrInException = services.every(
		(service) =>
			todayOfflineDays.some(
				(offlineDay) => offlineDay.service.id === service.id
			) ||
			(todayServicesExceptions &&
				todayServicesExceptions.services.some((s) => service.id === s.id))
	);

	if (anyServiceOfflineOrInException) return true;

	return false;
};

// FILTERING SCHEDULES ----------------------------------------------------------------

// This function filters the schedules received by anticipation.
export const filterSchedulesByAnticipation = (
	date: Date,
	schedules: string[],
	anticipation: number
) => {
	const filteredSchedules = schedules.filter((schedule) =>
		compareAnticipation(schedule, date, anticipation)
	);
	return filteredSchedules;
};

// GETTING ANTICIPATION  ----------------------------------------------------------------

export const getServiceAnticipation = (
	reservation: Reservation,
	services?: Service[]
) => {
	// If the category is restaurant, then the anticipation is the same for all that services.
	if (reservation.service) {
		return parseInt(reservation.service.category.anticipation ?? "0");
	} else {
		return parseInt(
			services!.find((s) => s.category.name === "Restaurant")?.category
				.anticipation ?? "0"
		);
	}
};

// FIND ACTIVE COMPONENT  ----------------------------------------------------------------
export const findActiveComponent = (selectedFlow: Flow, activeStep: number) =>
	selectedFlow.steps.find((step) => step.number === activeStep)!.component;

export const findComponentStep = (selectedFlow: Flow, name: string) =>
	selectedFlow.steps.find((s) => s.component === name)!.number;
